import { ClubPriceInfo } from './ClubPriceInfo';
import { PricePerUnitWidget } from './PricePerUnitWidget';
import { PromotionDuration } from './PromotionDuration';
import { VatAndTransportInfo } from './VatAndTransportInfo';
import { WarningBar } from 'components/Basic/WarningBar/WarningBar';
import { LoyaltyClubEarned } from 'components/Blocks/LoyaltyClubEarned/LoyaltyClubEarned';
import { B2bProductPrice } from 'components/Blocks/Product/Price/B2bProductPrice';
import WatchDog from 'components/Blocks/Product/WatchDog/WatchDog';
import Spinbox from 'components/Forms/Spinbox/Spinbox';
import { AddToCartButtonPrimary } from 'components/Pages/ProductDetail/ProductDetailAddToCart/AddToCartButtonPrimary';
import { mapAddToCartPopupData } from 'connectors/cart/Cart';
import { useSettings } from 'connectors/Settings/Settings';
import { isB2bDomain, isDomainLocale } from 'helpers/isDomainLocale';
import { useAddToCart } from 'hooks/cart/useAddToCart';
import { getSellingPriceByDomain, useFormatPrice } from 'hooks/formatting/useFormatPrice';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { createPersooEventForAddToCart } from 'modules/persoo/persooEvents';
import { useCallback, useRef } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { GtmListNameType } from 'types/gtm';
import { PricingHistoryStrategy } from 'types/price';
import { ProductDetailType, VariantDetailType } from 'types/product';
import twMerge from 'utils/twMerge';

type ProductDetailAddToCartProps = {
    gtmListName: GtmListNameType;
    product: ProductDetailType | VariantDetailType;
};

const addToCartPriceTwClass = 'leading-8 text-h2 text-red font-bold text-primary';

const ProductDetailAddToCart: FC<ProductDetailAddToCartProps> = ({ product }) => {
    const testIdentifier = 'pages-productdetail-addtocart';

    const spinboxRef = useRef<HTMLInputElement | null>(null);
    const t = useTypedTranslationFunction();
    const { isUserLoggedIn } = useCurrentUserData();
    const isSpecialPrice = product.price.priceWithVat < product.prices.basic.priceWithVat;
    const { formatPriceNumber, formatPriceObject } = useFormatPrice();
    const [changeCartItemQuantity, fetching] = useAddToCart('product');
    const domainConfig = useDomainConfig();
    const isBookingOnly = product.maximumOrderableQuantity <= 0;
    const { updateUserState } = usePersistStore((s) => s);
    const settings = useSettings();
    const isVatAndTransportInfoRequired = isDomainLocale(domainConfig.defaultLocale, 'de');

    const isReferencePriceRequired =
        (settings?.pricingHistoryStrategy === PricingHistoryStrategy.allExceptVouchers &&
            !product.isElectronicGiftVoucher &&
            !product.isVoucher) ||
        (settings?.pricingHistoryStrategy === PricingHistoryStrategy.discountedOnly && isSpecialPrice);

    const onAddToCartHandler = useCallback(async () => {
        if (spinboxRef.current === null) {
            return;
        }

        const newCartItemValue =
            spinboxRef.current.valueAsNumber -
            (spinboxRef.current.valueAsNumber % (product.b2bMinimumProductOrder ?? 1));
        const addToCartResult = await changeCartItemQuantity(product.uuid, null, newCartItemValue, 'detail');

        spinboxRef.current!.valueAsNumber = product.b2bMinimumProductOrder ?? 1;
        const mappedAddToCartResult = mapAddToCartPopupData(addToCartResult, domainConfig.currencyCode);

        if (mappedAddToCartResult) {
            createPersooEventForAddToCart(mappedAddToCartResult.catalogNumber);
            updateUserState({
                addToCartPopupData: {
                    gtmListName: 'detail',
                    listIndex: 1,
                    product: mappedAddToCartResult,
                },
            });
        }
    }, [
        changeCartItemQuantity,
        domainConfig.currencyCode,
        updateUserState,
        product.uuid,
        product.b2bMinimumProductOrder,
    ]);

    return (
        <>
            <div className="mb-4 flex flex-col gap-5 lg:mb-3" data-testid={testIdentifier}>
                <div>
                    {isB2bDomain(domainConfig) ? (
                        <B2bProductPrice
                            b2bPaymentDiscount={product.b2bPaymentDiscount}
                            sellingPrice={product.price}
                            basePrice={product.prices.basic}
                            variant="detail"
                            isUnitPrice={(product.b2bMinimumProductOrder ?? 0) > 1}
                        />
                    ) : (
                        <>
                            <div
                                className={twMerge(
                                    addToCartPriceTwClass,
                                    isSpecialPrice &&
                                        ' text-h6 font-medium leading-5 text-primary line-through decoration-red',
                                )}
                                data-testid={testIdentifier + '-price'}
                            >
                                <div className={twMerge(isVatAndTransportInfoRequired && 'mb-2 flex items-end')}>
                                    {formatPriceObject(product.prices.basic)}
                                    {isVatAndTransportInfoRequired && !isSpecialPrice && <VatAndTransportInfo />}
                                </div>
                                {!isSpecialPrice && !isUserLoggedIn && (
                                    <ClubPriceInfo
                                        price={product.price}
                                        prices={product.prices}
                                        isVatAndTransportInfoRequired
                                    />
                                )}
                            </div>
                            {!isSpecialPrice && product.referencePrice && isReferencePriceRequired && (
                                <div className="my-1 inline-block h-fit py-[6px] text-tiny leading-2">
                                    {`${t('Lowest price in the last 30 days')} `}
                                    {formatPriceNumber(product.referencePrice)}
                                </div>
                            )}
                            {isSpecialPrice && (
                                <>
                                    <div className="my-1 inline-block h-fit bg-red py-[6px] px-1 text-tiny leading-2 text-white">
                                        {`${t('You will save')} `}
                                        {formatPriceNumber(
                                            getSellingPriceByDomain(product.prices.basic, domainConfig) -
                                                getSellingPriceByDomain(product.price, domainConfig),
                                        )}
                                    </div>
                                    <div
                                        className={isVatAndTransportInfoRequired ? 'mb-3 flex items-end' : ''}
                                        data-testid={testIdentifier + '-price-special'}
                                    >
                                        <div className={addToCartPriceTwClass}>{formatPriceObject(product.price)}</div>
                                        <div className={addToCartPriceTwClass}>
                                            {isVatAndTransportInfoRequired && <VatAndTransportInfo />}
                                        </div>
                                        <PromotionDuration price={product.price} />
                                        {!isUserLoggedIn && (
                                            <div className={addToCartPriceTwClass}>
                                                <ClubPriceInfo
                                                    price={product.price}
                                                    prices={product.prices}
                                                    isVatAndTransportInfoRequired
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {isSpecialPrice && product.referencePrice && isReferencePriceRequired && (
                        <div className="my-1 inline-block h-fit py-[6px] text-tiny leading-2">
                            {`${t('Lowest price in the last 30 days')} `}
                            {formatPriceNumber(product.referencePrice)}
                        </div>
                    )}
                    {product.pricePerUnit && <PricePerUnitWidget pricePerUnit={product.pricePerUnit} />}
                    <LoyaltyClubEarned pointsWithPrice={product.loyaltyClubPointsObtained} className="!mb-0" />
                </div>
                {!product.isSellingDenied && (
                    <div className="leading">
                        {(product.b2bMinimumProductOrder ?? 0) > 1 && (
                            <div className="pb-2">
                                {t('Minimum order quantity {{ count }} pc', { count: product.b2bMinimumProductOrder })}
                            </div>
                        )}
                        <div className="flex items-stretch">
                            <Spinbox
                                id={product.uuid}
                                idSuffix="-detail"
                                min={product.b2bMinimumProductOrder ?? 1}
                                step={product.b2bMinimumProductOrder ?? 1}
                                defaultValue={product.b2bMinimumProductOrder ?? 1}
                                multiple={product.b2bMinimumProductOrder ?? 1}
                                max={product.maximumOrderableQuantity}
                                ref={spinboxRef}
                                overstepMessage={{
                                    tooHigh: t('You cannot add more items'),
                                    onlyMultiplesOf: t('Only multiples of {{ multiple }}', {
                                        multiple: product.b2bMinimumProductOrder ?? 1,
                                    }),
                                }}
                            />
                            <div className="ml-5 w-[calc(100%-146px)] flex-shrink-0 lg:w-auto">
                                <AddToCartButtonPrimary
                                    isDisabled={
                                        isB2bDomain(domainConfig) &&
                                        (product.maximumOrderableQuantity === 0 ||
                                            (product.b2bMinimumProductOrder ?? 0) > product.maximumOrderableQuantity)
                                    }
                                    onClick={onAddToCartHandler}
                                    testIdentifier={`${testIdentifier}-button`}
                                    isLoading={!isBookingOnly && fetching}
                                    isBookingOnly={isBookingOnly}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {product.isExcludedFromDelivery && (
                    <WarningBar>
                        {t(
                            'This product has shipping restrictions, so some shipping options in the next step may be limited.',
                        )}
                    </WarningBar>
                )}
                {(product.isSellingDenied || product.maximumOrderableQuantity <= 0) && !isB2bDomain(domainConfig) && (
                    <WatchDog productUuid={product.uuid} productName={product.name} />
                )}
            </div>
        </>
    );
};

export default ProductDetailAddToCart;
